import { watch, onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useLocale as useVuetifyLocale } from "vuetify";

export function useLocale() {
	const { locale: i18nLocale } = useI18n();

	const availableLocales = ref(["pt-BR", "en-US", "es-ES", "fr-FR"]);
	const currentLocale = computed(() => i18nLocale.value);

	// Mapear locales do i18n para locales do Vuetify
	const localeMap: Record<string, string> = {
		"pt-BR": "pt-BR",
		"en-US": "en-US",
		"es-ES": "es-ES",
		"fr-FR": "fr-FR",
	};

	// Função para obter o Vuetify locale com segurança
	const getVuetifyLocale = () => {
		try {
			const { current } = useVuetifyLocale();
			return current;
		} catch (error) {
			console.warn("Vuetify locale not available yet");
			return null;
		}
	};

	// Sincronizar locale do Vuetify com i18n
	const syncLocales = () => {
		const vuetifyLocale = getVuetifyLocale();
		const mappedLocale = localeMap[i18nLocale.value] || "pt-BR";

		// Só sincroniza se o Vuetify estiver disponível
		if (vuetifyLocale) {
			vuetifyLocale.value = mappedLocale;
		}

		// Sempre atualiza o atributo lang do HTML
		document.documentElement.lang = i18nLocale.value;
	};

	// Observar mudanças no locale do i18n
	watch(i18nLocale, syncLocales, { immediate: false });

	// Garantir que o locale seja sincronizado quando o componente for montado
	onMounted(() => {
		// Verificar se existe um locale salvo no localStorage
		const savedLocale = localStorage.getItem("locale");
		if (savedLocale && availableLocales.value.includes(savedLocale)) {
			i18nLocale.value = savedLocale;
		}

		// Tentar sincronizar agora, se não conseguir vai tentar novamente quando houver mudanças
		syncLocales();
	});

	const changeLocale = (newLocale: string) => {
		if (availableLocales.value.includes(newLocale)) {
			i18nLocale.value = newLocale;
		}
		localStorage.setItem("locale", newLocale);
		syncLocales();
	};

	return {
		locale: i18nLocale,
		currentLocale,
		availableLocales: availableLocales.value,
		changeLocale,
		syncLocales,
	};
}
